import { useState } from 'react'
import { Mail, ChevronDown, ChevronUp, Loader2, AlertCircle, Sparkles, User, AtSign, Tag } from 'lucide-react'
import { api } from '../../lib/api'

interface QaEmailInputProps {
  onThreadCreated: (threadId: string) => void
}

export function QaEmailInput({ onThreadCreated }: QaEmailInputProps) {
  const [rawEmail, setRawEmail] = useState('')
  const [title, setTitle] = useState('')
  const [senderName, setSenderName] = useState('')
  const [senderEmail, setSenderEmail] = useState('')
  const [showDetails, setShowDetails] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit() {
    if (!rawEmail.trim()) return
    setLoading(true)
    setError(null)

    try {
      const thread = await api.solutions.qa.createThread({
        rawEmail: rawEmail.trim(),
        title: title.trim() || undefined,
        senderName: senderName.trim() || undefined,
        senderEmail: senderEmail.trim() || undefined,
      })

      await api.solutions.qa.extractQuestions(thread.id)
      onThreadCreated(thread.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create thread')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-200 dark:border-white/10 flex-shrink-0">
        <div className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/30">
          <Mail className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Paste Email</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Paste an email to extract and answer questions
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Email Content <span className="text-red-500">*</span>
          </label>
          <textarea
            value={rawEmail}
            onChange={(e) => setRawEmail(e.target.value)}
            placeholder="Paste the email content here. The AI will extract all questions and generate answers..."
            rows={10}
            className="w-full px-3 py-2.5 text-sm bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none resize-y transition-colors"
          />
          <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
            {rawEmail.length > 0 ? `${rawEmail.length} characters` : 'Supports plain text and formatted email'}
          </p>
        </div>

        {/* Collapsible details section */}
        <div className="border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setShowDetails(!showDetails)}
            className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
          >
            <span>Additional Details</span>
            {showDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>

          {showDetails && (
            <div className="px-3 pb-3 space-y-3 border-t border-gray-100 dark:border-white/5">
              <div className="pt-3">
                <label className="flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  <Tag className="w-3 h-3" /> Title
                </label>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                  placeholder="Thread title (auto-generated if empty)"
                  className="w-full px-3 py-1.5 text-sm bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-colors" />
              </div>
              <div>
                <label className="flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  <User className="w-3 h-3" /> Sender Name
                </label>
                <input type="text" value={senderName} onChange={(e) => setSenderName(e.target.value)}
                  placeholder="Who sent this email?"
                  className="w-full px-3 py-1.5 text-sm bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-colors" />
              </div>
              <div>
                <label className="flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  <AtSign className="w-3 h-3" /> Sender Email
                </label>
                <input type="email" value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)}
                  placeholder="sender@example.com"
                  className="w-full px-3 py-1.5 text-sm bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-colors" />
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}
      </div>

      {/* Footer action */}
      <div className="px-5 py-3 border-t border-gray-200 dark:border-white/10 flex-shrink-0">
        <button
          onClick={handleSubmit}
          disabled={!rawEmail.trim() || loading}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Extracting Questions...</>
          ) : (
            <><Sparkles className="w-4 h-4" /> Extract Questions</>
          )}
        </button>
      </div>
    </div>
  )
}
